"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/posthog";

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Client-side error capture (review WX1 — error.tsx previously discarded
  // the error entirely). PostHog only fires when consent-gated init has run;
  // `trackEvent` no-ops otherwise. We also console.error so Vercel function
  // logs / browser consoles retain the stack during development.
  //
  // TODO(server-capture): there is no server-side error sink yet (no Sentry,
  // no posthog-node). When one is added, report `error.digest` server-side
  // too — the digest is the join key to the server log line Next.js emits.
  useEffect(() => {
    console.error("[error-boundary]", error);
    trackEvent("client_error", {
      digest: error.digest ?? null,
      name: error.name,
      message: error.message?.slice(0, 300) ?? null,
      pathname: typeof window !== "undefined" ? window.location.pathname : null,
      boundary: "route",
    });
  }, [error]);

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6">
      <div className="text-center max-w-[400px]">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-foreground text-3xl font-bold mb-2">Something Went Wrong</h1>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          An unexpected error occurred. Please try again or contact us if the problem persists.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={reset}
            className="bg-blue text-white font-semibold text-sm px-5 py-2.5 rounded-lg hover:opacity-90 transition cursor-pointer border-none"
          >
            Try Again
          </button>
          <a
            href="/"
            className="border border-surface-light/30 text-muted-foreground font-medium text-sm px-5 py-2.5 rounded-lg hover:bg-surface transition no-underline"
          >
            Go Home
          </a>
        </div>
      </div>
    </div>
  );
}
